import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../../api/api";
import toast from "react-hot-toast";
import { ArrowLeft, Save, ExternalLink, Trash2 } from "lucide-react";

const statusColors = {
  received: "bg-blue-50 text-blue-700",
  in_review: "bg-yellow-50 text-yellow-700",
  resolved: "bg-green-50 text-green-700",
  closed: "bg-slate-100 text-slate-700",
};

const Info = ({ label, value }) => (
  <div className="border border-slate-100 rounded-2xl p-4">
    <p className="text-xs uppercase tracking-wider text-slate-400 font-black">
      {label}
    </p>
    <p className="mt-1 font-bold text-slate-800 break-words">
      {value || "—"}
    </p>
  </div>
);

const AdminComplaintDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    status: "received",
    response_note: "",
    action_report: "",
    officer_name: "",
  });

  const loadComplaint = async () => {
    try {
      setLoading(true);

      const res = await api.get(`/admin/complaints/${id}`);
      setComplaint(res.data);

      setForm({
        status: res.data.status || "received",
        response_note: res.data.response_note || "",
        action_report: res.data.action_report || "",
        officer_name: res.data.officer_name || "",
      });
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load complaint");
      navigate("/admin/complaints");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComplaint();
  }, [id]);

  const updateComplaint = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      await api.put(`/admin/complaints/${id}`, form);

      toast.success("Complaint response updated");
      loadComplaint();
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to update complaint");
    } finally {
      setSaving(false);
    }
  };

  const deleteComplaint = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this complaint?"
    );

    if (!confirmDelete) return;

    try {
      await api.delete(`/admin/complaints/${id}`);
      toast.success("Complaint deleted");
      navigate("/admin/complaints");
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to delete complaint");
    }
  };

  if (loading || !complaint) {
    return <p>Loading complaint...</p>;
  }

  return (
    <div>
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
        <div>
          <Link
            to="/admin/complaints"
            className="inline-flex items-center gap-2 text-[#0b6b1d] font-bold mb-3"
          >
            <ArrowLeft size={18} />
            Back to complaints
          </Link>

          <h1 className="text-3xl font-black text-[#0b6b1d]">
            Complaint Details
          </h1>

          <p className="text-slate-500 mt-1">
            Tracking Number:{" "}
            <span className="font-black text-slate-800">
              {complaint.tracking_number}
            </span>
          </p>
        </div>

        <button
          onClick={deleteComplaint}
          className="px-5 py-3 rounded-2xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white font-black flex items-center gap-2"
        >
          <Trash2 size={18} />
          Delete
        </button>
      </div>

      <div className="grid xl:grid-cols-[1fr_420px] gap-6">
        <div className="space-y-6">
          <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4 mb-6">
              <h2 className="text-xl font-black text-[#0b6b1d]">
                Complaint Information
              </h2>

              <span
                className={`px-4 py-2 rounded-full text-xs font-black uppercase ${
                  statusColors[complaint.status] || "bg-slate-100 text-slate-700"
                }`}
              >
                {complaint.status?.replace("_", " ")}
              </span>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Info label="Complainant" value={complaint.name} />
              <Info label="Phone" value={complaint.phone} />
              <Info label="Email" value={complaint.email} />
              <Info label="School Affected" value={complaint.school_affected} />
              <Info label="LGA" value={complaint.lga} />
              <Info label="Complaint Type" value={complaint.complaint_type} />
              <Info label="Date Submitted" value={complaint.created_at} />
              <Info label="Last Updated" value={complaint.updated_at} />
            </div>

            <div className="mt-5 border border-slate-100 rounded-2xl p-5">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-black">
                Complaint Message
              </p>
              <p className="mt-2 text-slate-700 leading-relaxed whitespace-pre-wrap">
                {complaint.message}
              </p>
            </div>

            {complaint.evidence_url && (
              <a
                href={complaint.evidence_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 mt-5 text-[#0b6b1d] font-black border-b-2 border-[#0b6b1d] pb-1 hover:text-[#ff6600] hover:border-[#ff6600]"
              >
                View Uploaded Evidence <ExternalLink size={17} />
              </a>
            )}
          </div>

          {complaint.action_report && (
            <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
              <h2 className="text-xl font-black text-[#0b6b1d] mb-4">
                Internal Action Report
              </h2>

              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                {complaint.action_report}
              </p>

              <div className="mt-5 text-sm text-slate-500">
                Officer:{" "}
                <span className="font-black text-slate-700">
                  {complaint.officer_name || "—"}
                </span>
              </div>
            </div>
          )}
        </div>

        <form
          onSubmit={updateComplaint}
          className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm h-fit"
        >
          <h2 className="text-xl font-black text-[#0b6b1d] mb-5">
            Respond / Mark Action Taken
          </h2>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">Status</span>
            <select
              value={form.status}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, status: e.target.value }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
            >
              <option value="received">Received</option>
              <option value="in_review">In Review</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </label>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">
              Public Tracking Response
            </span>
            <textarea
              rows="4"
              value={form.response_note}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  response_note: e.target.value,
                }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
              placeholder="This can show when the user tracks their complaint."
            />
          </label>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">
              Internal Action Report
            </span>
            <textarea
              rows="7"
              value={form.action_report}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  action_report: e.target.value,
                }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
              placeholder="Write what was done. This report shows only inside admin."
            />
          </label>

          <label className="block mb-6">
            <span className="font-bold text-slate-700">
              Officer / Unit Name
            </span>
            <input
              value={form.officer_name}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  officer_name: e.target.value,
                }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
              placeholder="Example: Monitoring Unit"
            />
          </label>

          <button
            disabled={saving}
            className="w-full py-4 rounded-2xl bg-[#0b6b1d] hover:bg-[#ff6600] text-white font-black flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Save size={18} />
            {saving ? "Saving..." : "Save Response"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminComplaintDetails;